import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios retry interceptor for timeout resilience
axios.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;
    if (!config || config._retryCount >= 2) return Promise.reject(error);
    if (error.code === 'ECONNABORTED' || error.response?.status === 502 || error.response?.status === 504 || !error.response) {
      config._retryCount = (config._retryCount || 0) + 1;
      await new Promise(r => setTimeout(r, 500 * config._retryCount));
      return axios(config);
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      const { access_token, user } = response.data;
      setToken(access_token);
      setUser(user);
      localStorage.setItem('token', access_token);
      registerFCMToken(access_token);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };
  
  const registerFCMToken = async (authToken) => {
    try {
      if (!window.Capacitor?.isNativePlatform()) return;
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      const { receive } = await FirebaseMessaging.requestPermissions();
      if (receive === 'granted') {
        const { token: fcmToken } = await FirebaseMessaging.getToken();
        if (fcmToken) {
          await axios.post(`${API}/users/fcm-token`, { fcm_token: fcmToken }, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
        }
      }
    } catch {}
  };

  const register = async (username, password, displayName, inviteCode, email) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        username,
        password,
        display_name: displayName,
        email: email || '',
        invite_code: inviteCode
      });
      const { access_token, user, is_first_user } = response.data;
      setToken(access_token);
      setUser(user);
      localStorage.setItem('token', access_token);
      return { success: true, user, isFirstUser: is_first_user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const linkDeviceWithQR = async (linkToken) => {
    try {
      const response = await axios.post(`${API}/devices/link-via-qr`, 
        { link_token: linkToken },
        { params: { link_token: linkToken } }
      );
      const { access_token, user } = response.data;
      setToken(access_token);
      setUser(user);
      localStorage.setItem('token', access_token);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Device linking failed' 
      };
    }
  };

  const linkDeviceWithCode = async (pairingCode) => {
    try {
      const response = await axios.post(`${API}/devices/verify-pairing-code`, {
        pairing_code: pairingCode
      });
      const { access_token, user } = response.data;
      setToken(access_token);
      setUser(user);
      localStorage.setItem('token', access_token);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Invalid pairing code' 
      };
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    linkDeviceWithQR,
    linkDeviceWithCode
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};